/**
 * Asset Panel
 * Browse and manage image assets for use in designs
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Image,
  Upload,
  Trash2,
  Plus,
  FolderOpen,
  Search,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useResourceStore, useResourcesByCategory } from '@/stores/resourceStore';
import type { Resource, ResourceCategory, CreateResourceInput } from '@/types/resource';
import { useTemplateStore } from '@/stores';
import { nanoid } from 'nanoid';
import type { ImageLayer } from '@/types/layers';
import { isSvgSource, fetchAndParseSvg } from '@/lib/svg/parser';

const CATEGORY_LABELS: Record<ResourceCategory | 'all', string> = {
  all: 'All Assets',
  badges: 'Badges',
  devices: 'Device Mockups',
  icons: 'Icons',
  social: 'Social Media',
  decorative: 'Decorative',
  buttons: 'Buttons',
  custom: 'My Uploads',
};

export function AssetPanel() {
  const { resources, isLoading, loadResources, addResource, deleteResource, selectedCategory, setSelectedCategory } = useResourceStore();
  const { addLayer } = useTemplateStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load resources on mount
  useEffect(() => {
    loadResources();
  }, [loadResources]);

  // Filter resources by category and search
  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch = !searchQuery ||
      resource.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Handle file upload
  const handleFileUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file`);
        }

        // Read file as data URL
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });

        // Get image dimensions
        const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
          const img = new window.Image();
          img.onload = () => resolve({ width: img.width, height: img.height });
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = dataUrl;
        });

        // Add to resource store
        const input: CreateResourceInput = {
          name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
          type: 'image',
          category: 'custom',
          data: {
            src: dataUrl,
            width: dimensions.width,
            height: dimensions.height,
            mimeType: file.type,
          },
          isBuiltin: false,
        };

        await addResource(input);
      }
    } catch (error) {
      setUploadError((error as Error).message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [addResource]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  // Add asset to canvas as image layer
  const handleAddToCanvas = useCallback(async (resource: Resource) => {
    if (resource.type !== 'image') return;

    const isSvg = isSvgSource(resource.data.src);
    let svgContent: string | undefined;
    let detectedColors: string[] | undefined;

    // Parse SVG content if it's an SVG
    if (isSvg) {
      const parsed = await fetchAndParseSvg(resource.data.src);
      if (parsed) {
        svgContent = parsed.content;
        detectedColors = parsed.colors;
      }
    }

    const imageLayer: ImageLayer = {
      id: nanoid(),
      type: 'image',
      name: resource.name,
      position: { mode: 'alignment', alignment: 'Center' },
      width: Math.min(resource.data.width, 400),
      height: Math.min(resource.data.height, 400),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        src: resource.data.src,
        objectFit: 'contain',
        isSvg,
        svgContent,
        detectedColors,
        colorOverrides: {},
      },
    };
    addLayer(imageLayer);
  }, [addLayer]);

  // Delete custom asset
  const handleDelete = useCallback(async (resource: Resource) => {
    if (resource.isBuiltin) return;
    await deleteResource(resource.id);
  }, [deleteResource]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-fg-muted" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[200px_1fr] gap-6 h-full">
      {/* Left sidebar: Categories + Upload */}
      <div className="space-y-4">
        {/* Category filter */}
        <div>
          <h3 className="label">Categories</h3>
          <div className="space-y-1">
            {(Object.keys(CATEGORY_LABELS) as (ResourceCategory | 'all')[]).map((category) => {
              const count = category === 'all'
                ? resources.length
                : resources.filter(r => r.category === category).length;

              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`
                    w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between
                    ${selectedCategory === category
                      ? 'bg-primary/20 text-fg'
                      : 'text-fg-muted hover:bg-white/5 hover:text-fg'
                    }
                  `}
                >
                  <span>{CATEGORY_LABELS[category]}</span>
                  <span className="text-xs text-fg-subtle">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Upload section */}
        <div>
          <h3 className="label">Upload</h3>
          <div
            className={`
              border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer
              ${dragOver
                ? 'border-primary bg-primary/10'
                : 'border-white/20 hover:border-white/40'
              }
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? (
              <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-primary" />
            ) : (
              <Upload className="w-6 h-6 mx-auto mb-2 text-fg-muted" />
            )}
            <p className="text-xs text-fg-muted">
              {isUploading ? 'Uploading...' : 'Drop content here or click to upload'}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleFileUpload(e.target.files)}
            className="hidden"
          />

          {uploadError && (
            <div className="mt-2 p-2 bg-red-500/10 rounded-lg flex items-center gap-2 text-xs text-red-400">
              <AlertCircle size={14} />
              {uploadError}
            </div>
          )}
        </div>
      </div>

      {/* Right side: Asset grid */}
      <div>
        {/* Search bar */}
        <div className="relative mb-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search assets..."
            className="w-full bg-navy border border-white/10 rounded-lg pl-9 pr-8 py-2 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:border-primary/50"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Asset count */}
        <p className="text-xs text-fg-subtle mb-3">
          {filteredResources.length} asset{filteredResources.length !== 1 ? 's' : ''}
        </p>

        {/* Asset grid */}
        {filteredResources.length === 0 ? (
          <div className="text-center py-8 text-fg-subtle">
            <Image size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No assets found</p>
            {selectedCategory === 'custom' && (
              <p className="text-xs mt-1">Upload images to get started</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {filteredResources.map((resource) => (
              <AssetCard
                key={resource.id}
                resource={resource}
                onAddToCanvas={() => handleAddToCanvas(resource)}
                onDelete={() => handleDelete(resource)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface AssetCardProps {
  resource: Resource;
  onAddToCanvas: () => void;
  onDelete: () => void;
}

function AssetCard({ resource, onAddToCanvas, onDelete }: AssetCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative group bg-white/5 rounded-lg overflow-hidden border border-white/10 hover:border-primary/50 transition-colors"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image preview */}
      <div className="aspect-square flex items-center justify-center p-2 bg-navy/50">
        <img
          src={resource.data.src}
          alt={resource.name}
          className="max-w-full max-h-full object-contain"
          loading="lazy"
        />
      </div>

      {/* Name */}
      <div className="px-2 py-1.5 border-t border-white/5">
        <p className="text-xs text-fg truncate" title={resource.name}>
          {resource.name}
        </p>
        <p className="text-[10px] text-fg-subtle">
          {resource.data.width}×{resource.data.height}
        </p>
      </div>

      {/* Hover actions */}
      {isHovered && (
        <div className="absolute inset-0 bg-navy/80 flex items-center justify-center gap-2">
          <button
            onClick={onAddToCanvas}
            className="p-2 bg-primary hover:bg-primary/80 rounded-lg text-white transition-colors"
            title="Add to canvas"
          >
            <Plus size={16} />
          </button>
          {!resource.isBuiltin && (
            <button
              onClick={onDelete}
              className="p-2 bg-red-500/80 hover:bg-red-500 rounded-lg text-white transition-colors"
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      )}

      {/* Built-in badge */}
      {resource.isBuiltin && (
        <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-white/10 rounded text-[9px] text-fg-subtle">
          Built-in
        </div>
      )}
    </div>
  );
}
